//Built-in libraries

import { TrashIcon } from '@heroicons/react/24/outline';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
//Third party libraries
import * as Yup from 'yup';
import { HookSelect } from '@/components/UI/HookForm/Form/Select';
import { usesList } from '../../../../constants';
//Services
import { formatNumber, projectBudgetTotal } from '../../../../utils';
//Custom components
import Button from '../../../UI/Button';
import HookMaskedInput from '../../../UI/HookForm/Form/MaskedInput';
import { CurrencyFormatDisplay } from '../../../UI/NumberFormatDisplay/CurrencyFormatDisplay';

export const projectUsesValidationSchema = Yup.object({
  uses: Yup.array().of(
    Yup.object({
      name: Yup.string().required('This is required'),
      amount: Yup.number().required('This is required').min(0, 'Amount must be 0 or greater'),
      source: Yup.string().required('This is required'),
    })
  ),
});

const ProjectUsesHookFieldArray = ({ sources }: { sources: { name: string; amount: number }[] }) => {
  const { trigger, getValues } = useFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'uses' });
  const projectUsesFormValues = useWatch({ name: 'uses' });
  const budgetTotal = formatNumber(projectBudgetTotal(projectUsesFormValues));
  const sourceOptions = sources.filter((source) => source.name && source.amount >= 0);
  const hasSources = sourceOptions.length > 0;
  const addNewUse = async () => {
    if (!hasSources) return;

    const projectUsesValidation = await trigger('uses');
    if (projectUsesValidation || getValues('uses').length === 0) {
      append({ name: '', amount: 0, source: sourceOptions[0].name });
    }
  };

  return (
    <>
      {fields.length <= 0 && <p className='text-sm text-tertiaryColor-normal'>There are no uses added into the project yet.</p>}
      {fields.map((uses: any, idx) => {
        return (
          <div key={uses.id} className='flex items-center justify-between gap-4 my-4'>
            <div className='grid grid-cols-3 gap-5 w-full'>
              <HookSelect
                customClassName='col-span-1'
                label='Use'
                name={`uses.${idx}.name` as const}
                id='name'
                options={usesList.map((d) => ({
                  label: d,
                  value: d,
                }))}
                required={false}
              />
              <HookMaskedInput
                customClassName='col-span-1'
                label='Amount ($)'
                name={`uses.${idx}.amount` as const}
                id='amount'
                maskedComponent='currency'
              />
              <HookSelect
                customClassName='col-span-1'
                label='Paid From'
                name={`uses.${idx}.source` as const}
                id='source'
                options={sourceOptions.map((d) => ({
                  label: d.name,
                  value: d.name,
                }))}
                required={true}
              />
            </div>
            <div
              className='h-full border p-2 mt-6 rounded-md text-neutralColor-normal bg-gray-50 hover:bg-tertiaryColor-lightest hover:text-tertiaryColor-normal cursor-pointer transition duration-100 ease-in-out'
              onClick={() => remove(idx)}
            >
              <TrashIcon className='h-5 w-6' />
            </div>
          </div>
        );
      })}
      <p className='mt-5 font-bold text-primaryColor-normal'>
        Total: <CurrencyFormatDisplay value={budgetTotal} prefix='$ ' />
      </p>
      <div className='mt-8'>
        {!hasSources && <p className='text-sm text-tertiaryColor-normal'>Add a project source before adding uses.</p>}
        <Button label='Add Use' btnStyle='btn-primary' btnSize='btn-sm' type='button' disabled={!hasSources} onClick={addNewUse} />
      </div>
    </>
  );
};

export default ProjectUsesHookFieldArray;
